import { useState } from "react";
import {
  TextInput,
  Textarea,
  Select,
  PillsInput,
  Pill,
  Combobox,
  useCombobox,
  Button,
  Group,
  Stack,
  Tabs,
  Text,
  ColorSwatch,
  CheckIcon,
  Popover,
  Box,
  TypographyStylesProvider,
} from "@mantine/core";
import { IconMarkdown, IconEdit } from "@tabler/icons-react";
import Markdown from "react-markdown";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useTopics, useTags } from "../hooks/useApi";
import { tagService } from "../lib/services";
import { showErrorNotification } from "../lib/notifications";
import type { CreatePromptBody } from "../lib/types";

/**
 * Maps API error codes to user-friendly messages.
 * Keys match the `code` field returned by the API — ready for i18n lookup.
 */
const ERROR_MESSAGES: Record<string, string> = {
  PROMPT_SLUG_DUPLICATE: "A prompt with this slug already exists. Choose a different title or slug.",
  TOPIC_SLUG_DUPLICATE: "A topic with this slug already exists.",
  WORKSPACE_SLUG_DUPLICATE: "A workspace with this slug already exists.",
  DUPLICATE_RESOURCE: "A resource with this identifier already exists.",
  FREE_TIER_LIMIT: "You've reached the prompt limit for the free tier. Upgrade to Pro for unlimited prompts.",
  REFERENCED_NOT_FOUND: "The selected topic or tag no longer exists. Please refresh and try again.",
  INTERNAL_ERROR: "Something went wrong on our end. Please try again.",
};

const PRESET_COLORS = [
  "#868e96", "#fa5252", "#e64980", "#be4bdb", "#7950f2",
  "#4c6ef5", "#228be6", "#15aabf", "#12b886", "#40c057",
  "#82c91e", "#fab005", "#fd7e14",
];

function getErrorCode(err: unknown): string {
  if (err && typeof err === "object") {
    const e = err as Record<string, unknown>;
    // Primary: use the API error code
    if (typeof e.code === "string" && e.code !== "UNKNOWN") {
      return e.code;
    }
    // Fallback: map HTTP status to a code
    if (e.status === 409) return "DUPLICATE_RESOURCE";
    if (e.status === 403) return "FREE_TIER_LIMIT";
  }
  return "UNKNOWN";
}

interface PromptFormProps {
  onSubmit: (body: CreatePromptBody) => Promise<void>;
  onCancel: () => void;
}

export function PromptForm({ onSubmit, onCancel }: PromptFormProps) {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.id ?? null;
  const { data: topics } = useTopics(workspaceId);
  const { data: tags, refetch: refetchTags } = useTags(workspaceId);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [topicId, setTopicId] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [changelogNote, setChangelogNote] = useState("");
  const [selectedTagNames, setSelectedTagNames] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>("edit");

  // Tag combobox
  const [tagSearch, setTagSearch] = useState("");
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });

  // For new tag color picking
  const [newTagName, setNewTagName] = useState<string | null>(null);
  const [newTagColor, setNewTagColor] = useState(PRESET_COLORS[5]);

  const tagList = tags ?? [];

  function getTagColor(name: string): string | undefined {
    return tagList.find((t) => t.name === name)?.color ?? undefined;
  }

  function resolveTagIds(): string[] {
    return selectedTagNames
      .map((name) => tagList.find((t) => t.name === name)?.id)
      .filter((id): id is string => !!id);
  }

  function handleTagSelect(name: string) {
    if (selectedTagNames.includes(name)) {
      setSelectedTagNames((prev) => prev.filter((v) => v !== name));
    } else {
      // Check if this is a new tag (not in existing list)
      const exists = tagList.some((t) => t.name === name);
      if (!exists) {
        setNewTagName(name);
      } else {
        setSelectedTagNames((prev) => [...prev, name]);
      }
    }
    setTagSearch("");
  }

  function handleTagRemove(name: string) {
    setSelectedTagNames((prev) => prev.filter((v) => v !== name));
  }

  async function confirmNewTag() {
    if (!workspaceId || !newTagName) return;
    try {
      await tagService.create(workspaceId, { name: newTagName, color: newTagColor });
      refetchTags();
      setSelectedTagNames((prev) =>
        prev.includes(newTagName!) ? prev : [...prev, newTagName!],
      );
    } catch {
      showErrorNotification("Failed to create tag");
    } finally {
      setNewTagName(null);
      setNewTagColor(PRESET_COLORS[5]);
    }
  }

  async function handleSubmit() {
    if (!title.trim() || !content.trim()) return;
    setSubmitting(true);
    try {
      const body: CreatePromptBody = {
        title: title.trim(),
        content: content.trim(),
        ...(slug.trim() && { slug: slug.trim() }),
        ...(description.trim() && { description: description.trim() }),
        ...(topicId && { topicId }),
        ...(changelogNote.trim() && { changelogNote: changelogNote.trim() }),
      };
      const tagIds = resolveTagIds();
      if (tagIds.length > 0) body.tagIds = tagIds;
      await onSubmit(body);
    } catch (err) {
      const code = getErrorCode(err);
      const message = ERROR_MESSAGES[code] ?? "Failed to create prompt. Please try again.";
      showErrorNotification(message);
    } finally {
      setSubmitting(false);
    }
  }

  // Filter tag options by search
  const filteredTags = tagList.filter((t) =>
    t.name.toLowerCase().includes(tagSearch.trim().toLowerCase()),
  );
  const exactMatch = tagList.some(
    (t) => t.name.toLowerCase() === tagSearch.trim().toLowerCase(),
  );

  return (
    <Stack gap="md">
      <Text fw={600} size="lg">
        New Prompt
      </Text>

      <TextInput
        label="Title"
        placeholder="My awesome prompt"
        required
        value={title}
        onChange={(e) => setTitle(e.currentTarget.value)}
      />

      <TextInput
        label="Slug"
        placeholder="Auto-generated from title if left blank"
        value={slug}
        onChange={(e) => setSlug(e.currentTarget.value)}
      />

      <TextInput
        label="Description"
        placeholder="Brief description of what this prompt does"
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
      />

      <Select
        label="Topic"
        placeholder="Select a topic (optional)"
        clearable
        data={(topics ?? []).map((t) => ({ value: t.id, label: t.name }))}
        value={topicId}
        onChange={setTopicId}
      />

      <Box>
        <Combobox store={combobox} onOptionSubmit={handleTagSelect}>
          <Combobox.DropdownTarget>
            <PillsInput
              label="Tags"
              onClick={() => combobox.openDropdown()}
            >
              <Pill.Group>
                {selectedTagNames.map((name) => {
                  const color = getTagColor(name);
                  return (
                    <Pill
                      key={name}
                      withRemoveButton
                      onRemove={() => handleTagRemove(name)}
                      style={
                        color
                          ? {
                              backgroundColor: `${color}22`,
                              border: `1px solid ${color}55`,
                              color: color,
                            }
                          : undefined
                      }
                    >
                      {name}
                    </Pill>
                  );
                })}
                <Combobox.EventsTarget>
                  <PillsInput.Field
                    placeholder={selectedTagNames.length === 0 ? "Add tags..." : ""}
                    value={tagSearch}
                    onFocus={() => combobox.openDropdown()}
                    onBlur={() => combobox.closeDropdown()}
                    onChange={(e) => {
                      combobox.updateSelectedOptionIndex();
                      setTagSearch(e.currentTarget.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" && tagSearch.length === 0 && selectedTagNames.length > 0) {
                        e.preventDefault();
                        handleTagRemove(selectedTagNames[selectedTagNames.length - 1]);
                      }
                      if (e.key === "Enter" && tagSearch.trim() && !exactMatch) {
                        e.preventDefault();
                        handleTagSelect(tagSearch.trim());
                      }
                    }}
                  />
                </Combobox.EventsTarget>
              </Pill.Group>
            </PillsInput>
          </Combobox.DropdownTarget>

          <Combobox.Dropdown>
            <Combobox.Options>
              {filteredTags.map((tag) => (
                <Combobox.Option
                  value={tag.name}
                  key={tag.id}
                  active={selectedTagNames.includes(tag.name)}
                >
                  <Group gap="xs">
                    {selectedTagNames.includes(tag.name) && <CheckIcon size={12} />}
                    {tag.color && <ColorSwatch color={tag.color} size={14} />}
                    <span>{tag.name}</span>
                  </Group>
                </Combobox.Option>
              ))}
              {tagSearch.trim() && !exactMatch && (
                <Combobox.Option value={tagSearch.trim()}>
                  <Text size="sm">+ Create tag "{tagSearch.trim()}"</Text>
                </Combobox.Option>
              )}
              {filteredTags.length === 0 && !tagSearch.trim() && (
                <Combobox.Empty>No tags yet</Combobox.Empty>
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        {newTagName && (
          <Popover opened withArrow shadow="md">
            <Popover.Target>
              <Text size="sm" mt="xs" c="dimmed">
                Pick a color for tag "{newTagName}":
              </Text>
            </Popover.Target>
            <Popover.Dropdown>
              <Group gap="xs">
                {PRESET_COLORS.map((color) => (
                  <ColorSwatch
                    key={color}
                    color={color}
                    onClick={() => setNewTagColor(color)}
                    style={{ cursor: "pointer" }}
                    size={24}
                  >
                    {newTagColor === color && <CheckIcon size={12} />}
                  </ColorSwatch>
                ))}
              </Group>
              <Group mt="sm" justify="flex-end">
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => {
                    setNewTagName(null);
                    setNewTagColor(PRESET_COLORS[5]);
                  }}
                >
                  Cancel
                </Button>
                <Button size="xs" onClick={confirmNewTag}>
                  Create Tag
                </Button>
              </Group>
            </Popover.Dropdown>
          </Popover>
        )}
      </Box>

      <Box>
        <Text size="sm" fw={500} mb={4}>
          Content <span style={{ color: "var(--mantine-color-red-6)" }}>*</span>
        </Text>
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="edit" leftSection={<IconEdit size={14} />}>
              Edit
            </Tabs.Tab>
            <Tabs.Tab value="preview" leftSection={<IconMarkdown size={14} />}>
              Preview
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="edit" pt="xs">
            <Textarea
              placeholder="Write your prompt content here... Use {{variable}} for template variables."
              autosize
              minRows={10}
              value={content}
              onChange={(e) => setContent(e.currentTarget.value)}
              styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)" } }}
            />
          </Tabs.Panel>

          <Tabs.Panel value="preview" pt="xs">
            <Box
              p="md"
              style={{
                border: "1px solid var(--mantine-color-default-border)",
                borderRadius: "var(--mantine-radius-sm)",
                minHeight: 200,
              }}
            >
              {content.trim() ? (
                <TypographyStylesProvider>
                  <Markdown>{content}</Markdown>
                </TypographyStylesProvider>
              ) : (
                <Text c="dimmed" fs="italic">
                  Nothing to preview
                </Text>
              )}
            </Box>
          </Tabs.Panel>
        </Tabs>
        <Text size="xs" c="dimmed" mt={4}>
          Use {"{{variable}}"} syntax for template variables
        </Text>
      </Box>

      <TextInput
        label="Changelog Note"
        placeholder="Initial version (optional)"
        value={changelogNote}
        onChange={(e) => setChangelogNote(e.currentTarget.value)}
      />

      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={submitting}
          disabled={!title.trim() || !content.trim()}
        >
          Create Prompt
        </Button>
      </Group>
    </Stack>
  );
}
